import { useEffect, useRef, useState } from "react";
import { isAndroid, isIOS, isSafari } from "react-device-detect";
import PWAPrompt from "react-ios-pwa-prompt";

const SHOW_DELAY_MS = 3000;
const REPEAT_MS = 2 * 60 * 1000;

// Module-level so it survives re-renders
let androidDeferredPrompt = null;

const isStandalone = () =>
	window.matchMedia("(display-mode: standalone)").matches ||
	Boolean(window.navigator.standalone);

const PWARepeatPrompt = () => {
	const [iosMountKey, setIosMountKey] = useState(0);
	const [androidReady, setAndroidReady] = useState(false);
	const [androidVisible, setAndroidVisible] = useState(false);
	const [alreadyInstalled, setAlreadyInstalled] = useState(isStandalone);

	const iosEnabled = localStorage.getItem("enIOSPWAPopup") === "true";
	const androidReadyRef = useRef(androidReady);
	androidReadyRef.current = androidReady;

	// Android: capture beforeinstallprompt + detect post-install
	useEffect(() => {
		const onBefore = (e) => {
			e.preventDefault();
			androidDeferredPrompt = e;
			setAndroidReady(true);
		};
		const onInstalled = () => {
			androidDeferredPrompt = null;
			setAndroidReady(false);
			setAlreadyInstalled(true);
			setAndroidVisible(false);
		};
		window.addEventListener("beforeinstallprompt", onBefore);
		window.addEventListener("appinstalled", onInstalled);
		return () => {
			window.removeEventListener("beforeinstallprompt", onBefore);
			window.removeEventListener("appinstalled", onInstalled);
		};
	}, []);

	// Timer: initial delay then repeat every 2 minutes
	useEffect(() => {
		if (alreadyInstalled) return;

		const attempt = () => {
			if (isStandalone()) {
				setAlreadyInstalled(true);
				return;
			}
			// iOS: increment key to force-remount PWAPrompt (resets its visible state)
			if (iosEnabled && isIOS && isSafari) {
				setIosMountKey((k) => k + 1);
			}
			// Android: show custom bottom sheet
			if (isAndroid && androidReadyRef.current) {
				setAndroidVisible(true);
			}
		};

		const delay = setTimeout(attempt, SHOW_DELAY_MS);
		const interval = setInterval(attempt, REPEAT_MS);

		return () => {
			clearTimeout(delay);
			clearInterval(interval);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [alreadyInstalled, iosEnabled]);

	const handleInstallAndroid = () => {
		setAndroidVisible(false);
		if (androidDeferredPrompt) {
			androidDeferredPrompt.prompt();
			androidDeferredPrompt.userChoice.then(() => {
				androidDeferredPrompt = null;
				setAndroidReady(false);
			});
		}
	};

	const handleCloseAndroid = () => setAndroidVisible(false);

	if (alreadyInstalled || isStandalone()) return null;

	return (
		<>
			{/* iOS: original library popup, force-remounted every 2 min via key.
			    timesToShow=999999 + permanentlyHideOnDismiss=false bypass the one-time limit. */}
			{iosEnabled && isIOS && isSafari && iosMountKey > 0 && (
				<PWAPrompt
					key={iosMountKey}
					delay={0}
					timesToShow={999999}
					permanentlyHideOnDismiss={false}
					copyTitle={localStorage.getItem("iOSPWAPopupTitle")}
					copyBody={localStorage.getItem("iOSPWAPopupBody")}
					copyShareButtonLabel={localStorage.getItem("iOSPWAPopupShareButtonLabel")}
					copyAddHomeButtonLabel={localStorage.getItem("iOSPWAPopupAddButtonLabel")}
					copyClosePrompt={localStorage.getItem("iOSPWAPopupCloseButtonLabel")}
				/>
			)}

			{/* Android: custom bottom sheet shown every 2 min while deferredPrompt is available */}
			{isAndroid && androidReady && androidVisible && (
				<>
					<div
						style={{
							position: "fixed",
							inset: 0,
							backgroundColor: "rgba(0,0,0,0.5)",
							zIndex: 99998,
						}}
						onClick={handleCloseAndroid}
					/>
					<div
						style={{
							position: "fixed",
							bottom: 0,
							left: 0,
							right: 0,
							backgroundColor: "#fff",
							borderRadius: "16px 16px 0 0",
							padding: "24px 24px 36px",
							zIndex: 99999,
							textAlign: "center",
							boxShadow: "0 -6px 30px rgba(0,0,0,0.2)",
							direction: document.dir || "ltr",
						}}
					>
						<div
							style={{
								width: 36,
								height: 4,
								borderRadius: 2,
								backgroundColor: "#ddd",
								margin: "0 auto 16px",
							}}
						/>
						<div style={{ fontWeight: 700, fontSize: "1.05rem", marginBottom: 8 }}>
							{localStorage.getItem("pwaInstallFooterMessage") || "Install App"}
						</div>
						<button
							className="btn btn-success btn-lg mt-3"
							style={{ width: "100%" }}
							onClick={handleInstallAndroid}
						>
							{localStorage.getItem("pwaInstallFooterInstallText") || "Install"}
						</button>
						<button
							onClick={handleCloseAndroid}
							style={{
								marginTop: 10,
								background: "none",
								border: "none",
								color: "#999",
								fontSize: "0.85rem",
								cursor: "pointer",
								display: "block",
								width: "100%",
							}}
						>
							{localStorage.getItem("iOSPWAPopupCloseButtonLabel") || "Later"}
						</button>
					</div>
				</>
			)}
		</>
	);
};

export default PWARepeatPrompt;
